import {useCallback} from 'react'

import {omit} from '../../../../utils/omit'
import {apiUpdateView} from '../../../api/view/api-update-view'
import {useVerticalGroupedBy} from '../../../features/grouping/hooks/use-vertical-grouped-by'
import {useApiRequest} from '../../../hooks/use-api-request'
import {ViewStateActionTypes} from '../../../hooks/use-view-state-reducer/view-state-action-types'
import {useViews} from '../../../hooks/use-views'
import type {VerticalGroup} from '../../../models/vertical-group'

type ColumnWidthHookReturnType = {
  updateColumnLimit: (columnLimit: number | undefined) => Promise<void>
  columnLimit: number | undefined
}

export const useColumnLimit = (verticalGroup: VerticalGroup): ColumnWidthHookReturnType => {
  const {currentView, viewStateDispatch} = useViews()
  const {groupedByColumn} = useVerticalGroupedBy()

  const viewNumber = currentView?.number
  const columnId = groupedByColumn?.databaseId
  const columnValueId = verticalGroup.groupMetadata?.id
  const updateColumnLimit = useCallback(
    async (limit: number | undefined) => {
      if (typeof viewNumber !== 'number' || typeof columnId !== 'number' || !columnValueId || !currentView) return

      await apiUpdateView({
        viewNumber,
        view: {
          ...omit(currentView.serverViewState, ['id', 'number', 'createdAt', 'updatedAt', 'priority']),
          layoutSettings: {
            ...currentView.serverViewState.layoutSettings,
            board: {
              ...currentView.serverViewState.layoutSettings.board,
              columnLimits: {
                ...currentView.serverViewState.layoutSettings.board?.columnLimits,
                [columnId]: {
                  ...currentView.serverViewState.layoutSettings.board?.columnLimits?.[columnId],
                  [columnValueId]: limit,
                },
              },
            },
          },
        },
      })

      viewStateDispatch({
        type: ViewStateActionTypes.UpdateBoardColumnLimit,
        limit,
        columnDatabaseId: columnId,
        optionId: columnValueId,
        viewNumber,
      })
    },
    [viewNumber, columnId, columnValueId, currentView, viewStateDispatch],
  )

  const {perform} = useApiRequest({
    request: updateColumnLimit,
  })

  return {
    updateColumnLimit: perform,
    columnLimit:
      columnId && columnValueId
        ? currentView?.localViewStateDeserialized.layoutSettings.board?.columnLimits?.[columnId]?.[columnValueId]
        : undefined,
  }
}
